import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { formatNumberGroupingSeparators } from '@vmos2/core';
import { Text, Separator, Button } from '@vmos2/design-system';
import { Price, usePriceAriaLabel } from '@vmos2/price';
import { selectSlug } from '@vmos2/utils';
import currency from 'currency.js';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

// @ts-ignore
import { ServiceChargePicker } from './serviceChargePicker';

type Props = {
  className?: string,
  discount: {
    discountedAmount: number,
    promotions: [
      {
        promoCode: any,
        promoCodeRequired: any,
        approved: unknown,
        name: string,
        userReward?: Object,
        requiresReward: boolean,
      }
    ],
    clearPromotion?: Function,
    discountCode: string,
    discountName: string,
    isDiscountCodeHidden: boolean,
    hasMultipleSelect?: boolean,
    hasAccountPromotion: boolean,
  },
  rewards: {
    isPayingWithPoints?: boolean,
    loyaltyData?: {
      slug: string,
      spend: {
        pointsPerPromo: number,
      },
    },
  },
  orderWithDelivery: boolean,
  amountWithoutDiscount: number,
  totalAmount: number,
  totalAmountOnlyOnDiscounts: boolean,
  totalBeforeTax: number,
  deliveryFee: number,
  salesTaxRate: number,
  taxAmount: number,
  giftCard: {
    balance: number,
    product_name: string,
    discount: number,
  },
  serviceCharge: {
    options: number[],
    selectedPrice: number | null,
    onChange: (option: number | null) => void,
  },
  optionalTipSetting: {
    values: number[],
    required: boolean,
  },
  clearMealVoucher: Function,
  clearPointsToCash: () => void,
  canRemoveMealVoucher: boolean,
  serviceChargeReadOnly: boolean,
  clearGiftCard: Function,
  showRewardRemoveBtn: Function,
  disableRewardRemoveBtn: boolean,
  canRemoveGiftCard?: boolean,
  fontSize?: 'md' | 'lg' | 'xl' | 'xxl',
  hideServiceCharge?: boolean,
  mealVouchers: [{ amount: number }],
  mealVouchersAmount: number,
  cashFromPointsExchangedAmount: number,
  processingOrder: boolean,
};

export const Subtotal = memo(
  ({
    amountWithoutDiscount,
    canRemoveGiftCard = true,
    canRemoveMealVoucher = true,
    cashFromPointsExchangedAmount,
    className,
    clearGiftCard,
    clearMealVoucher,
    clearPointsToCash,
    deliveryFee,
    disableRewardRemoveBtn,
    discount,
    fontSize = 'lg',
    giftCard,
    hideServiceCharge,
    mealVouchers,
    mealVouchersAmount,
    optionalTipSetting,
    orderWithDelivery,
    processingOrder,
    rewards,
    salesTaxRate,
    serviceCharge,
    serviceChargeReadOnly = true,
    showRewardRemoveBtn,
    taxAmount,
    totalAmount,
    totalAmountOnlyOnDiscounts,
    totalBeforeTax,
  }: Props) => {
    const getPriceAriaLabel = usePriceAriaLabel({ isNegative: true });
    const { t } = useTranslation('cart');
    const { t: tCommon } = useTranslation('common');
    const theme = useTheme();
    const hasPromoCode =
      discount?.promotions?.some(promo => promo.promoCode?.code) ||
      discount?.promotions?.some(promo => !promo.promoCodeRequired && promo.name);
    const hasUserReward = discount?.promotions?.some(promo => promo.userReward && promo.requiresReward);
    const hasApprovedDiscount = discount?.promotions?.some(promo => {
      if (promo.promoCode?.requiresPhone) {
        return promo.approved;
      }
      return true;
    });
    const discountedAmount = get(discount, 'discountedAmount');

    const serviceChargeSetting = get(serviceCharge, 'setting');
    const optionalServiceChargePrice = get(serviceCharge, 'selectedPrice');
    const serviceChargeOptional = get(serviceChargeSetting, 'options', []);
    const serviceChargeDefault = get(serviceChargeSetting, 'default');
    const hasOnlyOneOptionalServiceCharge = serviceChargeOptional.length === 1;

    const hasSalesTax = !isNil(salesTaxRate);
    const hasDeliveryFee = !!(orderWithDelivery && deliveryFee);
    const hasDiscount = (hasApprovedDiscount || rewards.isPayingWithPoints) && discountedAmount > 0;
    const hasOptionalServiceCharge = serviceChargeReadOnly
      ? typeof optionalServiceChargePrice === 'number' && optionalServiceChargePrice > 0
      : !isEmpty(get(serviceChargeSetting, 'options'));
    const hasTipsEnabled = !!get(optionalTipSetting, 'values', []).length;

    const hasSubTotal =
      hasDiscount ||
      hasDeliveryFee ||
      hasOptionalServiceCharge ||
      hasTipsEnabled ||
      giftCard ||
      mealVouchers ||
      !!cashFromPointsExchangedAmount;

    const showRewards = !!(hasSubTotal && rewards.isPayingWithPoints) && discountedAmount > 0;
    const showDiscountCode =
      !showRewards &&
      !!(
        hasSubTotal &&
        hasDiscount &&
        hasApprovedDiscount &&
        (hasPromoCode || hasUserReward || discount.hasAccountPromotion)
      );
    const hasTotal = totalAmount > 0;
    const isOnlyOneServiceCharge = hasOptionalServiceCharge && hasOnlyOneOptionalServiceCharge;
    const isServiceChargeReadOnly =
      serviceChargeReadOnly || (isOnlyOneServiceCharge && serviceChargeOptional[0] === serviceChargeDefault);
    const hasMealVouchers = !!mealVouchers?.length;

    const TextLevels = {
      xxl: 1,
      xl: 2,
      lg: 3,
      md: 4,
    };
    const TotalLevel = {
      xxl: 1,
      xl: 1,
      lg: 3,
      md: 4,
    };
    const textLevel = TextLevels[fontSize];

    const showTotal = totalAmountOnlyOnDiscounts
      ? showDiscountCode ||
        hasOptionalServiceCharge ||
        giftCard ||
        mealVouchers ||
        !!cashFromPointsExchangedAmount ||
        rewards.isPayingWithPoints
      : true;

    const cartTotalPrice: currency = currency(totalAmount)
      .subtract(get(giftCard, 'discount'))
      .subtract(mealVouchersAmount)
      .subtract(cashFromPointsExchangedAmount);

    const discountCodeName = t(
      discount.isDiscountCodeHidden ? 'checkout.discounts:discount' : 'checkout.discounts:applied',
      { code: discount.discountName }
    );

    const discountRewardName = `${t('checkout:discounts:rewards:points', {
      slug: selectSlug(rewards?.loyaltyData?.slug || '', tCommon('point', { context: 'plural' })),
    })} (${formatNumberGroupingSeparators(rewards?.loyaltyData?.spend?.pointsPerPromo || 0)})`;

    return (
      <div className={className} data-test="subtotal">
        {hasSubTotal && (
          <>
            <Separator />

            <Text align="spaceBetween" groupContentForScreenReader level={textLevel} margin="small">
              <span>{t('checkout:subTotal')} </span>
              <Price dataTest="subtotal-price">{amountWithoutDiscount}</Price>
            </Text>
          </>
        )}

        {!hideServiceCharge && (
          <>
            {hasOptionalServiceCharge && !hasTipsEnabled && (
              <Text align="spaceBetween" margin="small" level={textLevel}>
                <ServiceChargePicker
                  setting={serviceChargeSetting}
                  onChange={get(serviceCharge, 'onChange', noop)}
                  readOnly={isServiceChargeReadOnly}
                  selectedOption={get(serviceCharge, 'selectedOption')}
                />

                <span data-test="service-fee">
                  {!!optionalServiceChargePrice && optionalServiceChargePrice > 0 && (
                    <Price>{optionalServiceChargePrice}</Price>
                  )}
                </span>
              </Text>
            )}

            {!!(hasTipsEnabled && optionalServiceChargePrice) && (
              <Text align="spaceBetween" groupContentForScreenReader level={textLevel} margin="small">
                <span>{t('tip')} </span>
                <Price dataTest="tip">{optionalServiceChargePrice}</Price>
              </Text>
            )}
          </>
        )}

        {hasDeliveryFee && (
          <Text align="spaceBetween" groupContentForScreenReader level={textLevel} margin="small">
            <span>{t('checkout:deliveryFee')} </span>
            <Price dataTest="delivery-fee">{deliveryFee}</Price>
          </Text>
        )}

        {showDiscountCode && (
          <Text
            align="spaceBetween"
            color={theme.color.success}
            css={{ position: 'relative' }}
            dataTest="applied-discount"
            level={textLevel}
            mb={3}
          >
            <Discount>
              <p className="hide-text-show-focus" data-test="applied-discount-hidden-text">
                {`${discountCodeName}, ${getPriceAriaLabel(discountedAmount)}`}
              </p>

              <DiscountText aria-hidden="true" data-test="discount-code">
                {discountCodeName}
              </DiscountText>

              {discount.clearPromotion && !discount?.hasMultipleSelect ? (
                <Button
                  color={theme.color.grey}
                  data-test="remove-discount"
                  mode="link"
                  onClick={() => discount.clearPromotion!()}
                  disabled={processingOrder}
                >
                  {t('checkout.discounts:remove')}
                </Button>
              ) : null}
            </Discount>

            <Price ariaHidden dataTest="discounted-amount" isNegative>
              {discountedAmount}
            </Price>
          </Text>
        )}

        {showRewards && (
          <Text
            align="spaceBetween"
            color={theme.color.success}
            css={{ position: 'relative' }}
            level={textLevel}
            mb={3}
          >
            <Discount>
              <p className="hide-text-show-focus" data-test="applied-rewards-hidden-text">
                {`${discountRewardName}, ${getPriceAriaLabel(discountedAmount)}`}
              </p>

              <DiscountText aria-hidden="true" data-test="discount-rewards">
                {discountRewardName}
              </DiscountText>

              {showRewardRemoveBtn && discount.clearPromotion ? (
                <Button
                  color={theme.color.grey}
                  data-test="remove-discount"
                  disabled={disableRewardRemoveBtn}
                  mode="link"
                  onClick={() => discount.clearPromotion!()}
                >
                  {t('checkout.discounts:remove')}
                </Button>
              ) : null}
            </Discount>

            <Price ariaHidden dataTest="discount-rewards-points" isNegative>
              {discount.discountedAmount}
            </Price>
          </Text>
        )}

        {giftCard && (
          <Text
            align="spaceBetween"
            color={theme.color.success}
            css={{ position: 'relative' }}
            level={textLevel}
            mb={3}
          >
            <Discount>
              <p className="hide-text-show-focus" data-test="applied-gift-card-hidden-text">
                {`${t('checkout:discounts.giftCard')}, ${getPriceAriaLabel(giftCard.discount)}`}
              </p>

              <DiscountText aria-hidden="true" data-test="discount-gift-card">
                {t('checkout:discounts.giftCard')}
              </DiscountText>

              {canRemoveGiftCard && (
                <Button color={theme.color.grey} data-test="remove-gift-card" mode="link" onClick={clearGiftCard}>
                  {t('checkout.discounts:remove')}
                </Button>
              )}
            </Discount>

            <Price ariaHidden dataTest="gift-card" isNegative>
              {giftCard.discount}
            </Price>
          </Text>
        )}

        {hasMealVouchers &&
          mealVouchers.map(mealVoucher => (
            <Text
              align="spaceBetween"
              color={theme.color.success}
              css={{ position: 'relative' }}
              level={textLevel}
              mb={3}
            >
              <Discount>
                <p className="hide-text-show-focus" data-test="applied-meal-voucher-hidden-text">
                  {`${t('checkout:discounts.mealVoucher')}, ${getPriceAriaLabel(mealVoucher.amount)}`}
                </p>

                <DiscountText aria-hidden="true" data-test="discount-meal-voucher">
                  {t('checkout:discounts.mealVoucher')}
                </DiscountText>

                {canRemoveMealVoucher && (
                  <Button color={theme.color.grey} mode="link" onClick={clearMealVoucher}>
                    {t('checkout.discounts:remove')}
                  </Button>
                )}
              </Discount>

              <Price ariaHidden isNegative>
                {mealVoucher.amount}
              </Price>
            </Text>
          ))}

        {!!cashFromPointsExchangedAmount && (
          <Text
            align="spaceBetween"
            color={theme.color.success}
            css={{ position: 'relative' }}
            level={textLevel}
            mb={3}
          >
            <Discount>
              <p className="hide-text-show-focus" data-test="applied-cash-hidden-text">
                {`${t('checkout:discounts.cashFromPointsExchanged')}, ${getPriceAriaLabel(
                  cashFromPointsExchangedAmount
                )}`}
              </p>

              <DiscountText aria-hidden="true">{t('checkout:discounts.cashFromPointsExchanged')}</DiscountText>

              {clearPointsToCash && (
                <Button
                  color={theme.color.grey}
                  data-test="remove-cash-from-points-exchanged"
                  mode="link"
                  onClick={() => clearPointsToCash()}
                >
                  {t('checkout.discounts:remove')}
                </Button>
              )}
            </Discount>

            <Price ariaHidden isNegative>
              {cashFromPointsExchangedAmount}
            </Price>
          </Text>
        )}

        {hasSalesTax && (
          <>
            <Separator />

            <Text align="spaceBetween" groupContentForScreenReader margin="small" level={textLevel}>
              <span>{t('checkout:totalBeforeTax')} </span>
              <Price dataTest="total-before-tax">{totalBeforeTax}</Price>
            </Text>

            <Text align="spaceBetween" groupContentForScreenReader margin="small">
              <span>{t('checkout:tax')} </span>
              <Price dataTest="tax">{taxAmount}</Price>
            </Text>
          </>
        )}

        {(hasTotal || hasSubTotal) && showTotal && !hideServiceCharge && (
          <>
            <Separator />

            <Text
              align="spaceBetween"
              dataTest="cart-total"
              groupContentForScreenReader
              level={TotalLevel[fontSize]}
              strong
            >
              <span>{t('orderConfirmationV2:total')} </span>
              <Price dataTest="cart-total-price">{cartTotalPrice.value > 0 ? cartTotalPrice : currency(0)}</Price>
            </Text>
          </>
        )}
      </div>
    );
  }
);

const Discount = styled.div`
  min-width: 0;
  margin-right: 20px;
`;

const DiscountText = styled.span`
  display: inline-block;
  overflow-wrap: break-word;
  margin: 0 ${({ theme }) => theme.margins.xs} 4px 0;
`;
